import type { BookingResponse } from '@/core/api/models/bookings';
import type { ProfileDetailsResponse } from '@/core/api/models/profile';
import type { AvailableUnit } from '@/core/api/models/properties';
import type { PendingUnitReservation } from '@/core/stores/appStore';
import {
  extractBookingDetails,
  readBookingText,
} from '../services/bookingService';

const DEFAULT_RESERVATION_STATUS = 'Booking form Sent';

/** Booking creation timestamps only — never use expiry or unrelated dates. */
const BOOKING_REQUESTED_ON_FIELDS = [
  'requested_on',
  'created_date',
  'created_at',
] as const;

/** Format `requested_on` / `created_date` for reservation summary UI. */
export function formatBookingRequestedOn(value: string): string {
  if (!value.trim()) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.trim();
  }
  // Date and time formatted separately — some ICU locales join combined formats with "at".
  const datePart = new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
  const timePart = new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .format(date)
    .toUpperCase();
  return `${datePart}, ${timePart}`;
}

/** Profile ISO timestamp or pending store value for a unit's reservation date. */
export function readUnitReservationDateRaw(
  unitId: string,
  profile: ProfileDetailsResponse | null | undefined,
  pending: PendingUnitReservation | null,
): string {
  const profileRaw =
    profile?.unit_id === unitId ? readProfileBookingRequestedOnRaw(profile) : '';
  if (profileRaw) {
    return profileRaw;
  }
  if (pending?.unitId === unitId) {
    return pending.requestedOn?.trim() ?? '';
  }
  return '';
}

/** Bottom-sheet "Requested on" — profile ISO is formatted; store value is already formatted. */
export function resolveReservationRequestedOnDisplay(
  unitId: string,
  profile: ProfileDetailsResponse | null | undefined,
  pending: PendingUnitReservation | null,
): string {
  const profileRaw =
    profile?.unit_id === unitId ? readProfileBookingRequestedOnRaw(profile) : '';
  if (profileRaw) {
    return formatBookingRequestedOn(profileRaw);
  }
  if (pending?.unitId === unitId) {
    return pending.requestedOn?.trim() ?? '';
  }
  return '';
}

/** Status card calendar line — prefers profile ISO; falls back to pending store value. */
export function resolveReservationCardDateDisplay(
  unitId: string,
  profile: ProfileDetailsResponse | null | undefined,
  pending: PendingUnitReservation | null,
): string {
  const raw = readUnitReservationDateRaw(unitId, profile, pending);
  return raw ? formatReservationCardDate(raw) : '';
}

/** Long date for reservation status card, e.g. "26 August 2026". */
export function formatReservationCardDate(value: string): string {
  if (!value.trim()) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(date);
  } catch {
    return '';
  }
}

/** `requested_on` from GET /v1/customers/profile-details (booking creation). */
export function readProfileBookingRequestedOnRaw(
  profile: ProfileDetailsResponse | null | undefined,
): string {
  if (!profile) {
    return '';
  }
  const bookingDate =
    typeof profile.booking_date === 'string' ? profile.booking_date.trim() : '';
  if (bookingDate) {
    return bookingDate;
  }
  const signedDate =
    typeof profile.booking_form_signed_date === 'string'
      ? profile.booking_form_signed_date.trim()
      : '';
  return signedDate;
}

/**
 * Prefer profile-details `current_status` (or `status` when the login API
 * returned `convertedLead: true`) when it matches this booking unit
 * (raw English API value — UI maps to message only). Otherwise keep booking status.
 */
export function resolveReservationStatusFromBookingAndProfile(
  bookingUnitId: string,
  bookingReservationStatus: string,
  profile: ProfileDetailsResponse | null | undefined,
  convertedLead: boolean = false,
): string {
  const profileUnitId = profile?.unit_id?.trim() ?? '';
  const profileStatus = (convertedLead ? profile?.status : profile?.current_status)?.trim() ?? '';
  const shouldOverlayStatus =
    Boolean(profileUnitId) &&
    profileUnitId === bookingUnitId &&
    Boolean(profileStatus) &&
    !isInactiveReservationStatus(profileStatus);

  return shouldOverlayStatus ? profileStatus : bookingReservationStatus;
}

/**
 * Merge profile-details booking fields onto an active pending reservation.
 * - Requested-on: GET /v1/bookings `requested_on`, else profile `booking_date`
 * - Status: profile `current_status` when it matches this unit (raw English API
 *   value — store as-is; never pass through i18n; UI maps → message only)
 */
export function overlayProfileBookingOnPending(
  pending: PendingUnitReservation,
  profile: ProfileDetailsResponse | null | undefined,
  formatRequestedOn: (raw: string) => string = (raw) => raw,
  bookingRequestedOnRaw = '',
  convertedLead: boolean = false,
): PendingUnitReservation {
  const profileUnitId = profile?.unit_id?.trim() ?? '';
  let requestedOn = '';
  const bookingRaw = bookingRequestedOnRaw.trim();
  if (bookingRaw) {
    requestedOn = formatRequestedOn(bookingRaw);
  } else if (profileUnitId === pending.unitId) {
    const profileRaw = readProfileBookingRequestedOnRaw(profile);
    if (profileRaw) {
      requestedOn = formatRequestedOn(profileRaw);
    }
  }

  const reservationStatus = resolveReservationStatusFromBookingAndProfile(
    pending.unitId,
    pending.reservationStatus ?? '',
    profile,
    convertedLead,
  );

  if (reservationStatus === pending.reservationStatus) {
    return {
      ...pending,
      requestedOn,
    };
  }

  // Keep the API literal (e.g. "SPA Counter Signed") — do not translate.
  return {
    ...pending,
    requestedOn,
    reservationStatus,
    unit: {
      ...pending.unit,
      reservation_status: reservationStatus,
    },
  };
}

/** Fields we care about from GET /v1/bookings — API is the only source of truth. */
export interface AdhaActiveBooking {
  readonly unitId: string;
  readonly reservationStatus: string;
  readonly parentId: string;
  readonly requestedOnRaw: string;
  readonly unitNumberHint: string;
}

function isHttpEnvelopeStatus(value: string): boolean {
  return /^(success|ok|error|failed|failure)$/i.test(value);
}

/**
 * BE no-booking response:
 * `{ status: "success", message: "Booking not found.", details: null }`
 */
export function isBookingNotFoundResponse(
  response: BookingResponse | null | undefined,
): boolean {
  if (!response) {
    return false;
  }
  const message =
    typeof response.message === 'string' ? response.message.trim() : '';
  if (/booking not found/i.test(message)) {
    return true;
  }
  const status =
    typeof response.status === 'string' ? response.status.trim().toLowerCase() : '';
  return status === 'success' && response.details == null;
}

function seedAvailableUnitIfMissing(
  unit: AvailableUnit,
  availableUnits: readonly AvailableUnit[],
  upsertAvailableUnitFirst: (unit: AvailableUnit) => void,
): void {
  const alreadyInListing = availableUnits.some(
    (row) => row.unit_id === unit.unit_id,
  );
  if (!alreadyInListing) {
    upsertAvailableUnitFirst(unit);
  }
}

function isInactiveReservationStatus(status: string): boolean {
  const normalized = status.trim().toLowerCase();
  return normalized.includes('cancel') || normalized.includes('reject');
}

function readReservationStatus(booking: Record<string, unknown>): string {
  const explicit = readBookingText(booking, [
    'reservation_status',
    'booking_status',
  ]);
  if (explicit) {
    return explicit;
  }
  const fallback = readBookingText(booking, ['status']);
  if (!fallback || isHttpEnvelopeStatus(fallback)) {
    return '';
  }
  return fallback;
}

function readBookingNumber(
  booking: Record<string, unknown>,
  keys: readonly string[],
): number {
  for (const key of keys) {
    const value = booking[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return 0;
}

/**
 * Build a catalogue listing row from GET /v1/bookings details, e.g.:
 * `{ lead_id, unit_id, unit_number, unit_type, total_price, total_area, handover_date, ... }`
 */
export function availableUnitFromBookingRecord(
  booking: Record<string, unknown>,
  reservationStatus?: string,
): AvailableUnit | null {
  const unitId = readBookingText(booking, ['unit_id', 'unitId', 'property_id']);
  if (!unitId) {
    return null;
  }

  const unitNumber =
    readBookingText(booking, ['unit_number', 'unitNumber']) || unitId;
  const unitType = readBookingText(booking, ['unit_type', 'unitType']);
  const unitModel = readBookingText(booking, ['unit_model']);
  const handoverDate = readBookingText(booking, [
    'handover_date',
    'handoverDate',
  ]);
  const status =
    reservationStatus ||
    readReservationStatus(booking) ||
    DEFAULT_RESERVATION_STATUS;

  return {
    unit_id: unitId,
    unit_number: unitNumber,
    unit_type: unitType || '',
    ...(unitModel ? { unit_model: unitModel } : {}),
    number_of_bedrooms: readBookingText(booking, [
      'number_of_bedrooms',
      'bedrooms',
    ]),
    number_of_bathrooms: readBookingNumber(booking, [
      'number_of_bathrooms',
      'bathrooms',
    ]),
    total_price: readBookingNumber(booking, ['total_price', 'totalPrice']),
    total_area: readBookingNumber(booking, ['total_area', 'totalArea']),
    latitude: readBookingNumber(booking, ['latitude']),
    longitude: readBookingNumber(booking, ['longitude']),
    ...(handoverDate ? { handover_date: handoverDate } : {}),
    reservation_status: status,
  };
}

/**
 * Parse GET booking body into an active booking, or `null` when there is none.
 * - "Booking not found." (200, details null) → null
 * - Cancelled / rejected / missing unit → null (UI must clear)
 */
export function parseActiveBookingFromApi(
  response: BookingResponse | null | undefined,
): AdhaActiveBooking | null {
  if (isBookingNotFoundResponse(response)) {
    return null;
  }

  const booking = extractBookingDetails(response);
  if (!booking) {
    return null;
  }

  const unitId = readBookingText(booking, ['unit_id', 'unitId', 'property_id']);
  if (!unitId) {
    return null;
  }

  const reservationStatus = readReservationStatus(booking);
  if (isInactiveReservationStatus(reservationStatus)) {
    return null;
  }

  return {
    unitId,
    reservationStatus: reservationStatus || DEFAULT_RESERVATION_STATUS,
    parentId: readBookingText(booking, [
      'parent_id',
      'booking_id',
      'reservation_id',
      'lead_id',
      'id',
    ]),
    requestedOnRaw: readBookingText(booking, BOOKING_REQUESTED_ON_FIELDS),
    unitNumberHint: readBookingText(booking, [
      'unit_number',
      'unitNumber',
      'name',
    ]),
  };
}

export interface BuildPendingReservationOptions {
  readonly availableUnits: readonly AvailableUnit[];
  readonly previous: PendingUnitReservation | null;
  readonly resolveUnitTitle: (unit: AvailableUnit) => string;
  readonly formatRequestedOn?: (raw: string) => string;
  /** Prefer this row (from booking details) when seeding listing. */
  readonly bookingListingUnit?: AvailableUnit | null;
}

function resolveUnitForBooking(
  booking: AdhaActiveBooking,
  listingUnit: AvailableUnit | undefined,
  previousSameUnit: PendingUnitReservation | null,
  bookingListingUnit?: AvailableUnit | null,
): AvailableUnit {
  if (listingUnit && bookingListingUnit) {
    return {
      ...listingUnit,
      ...bookingListingUnit,
      reservation_status: booking.reservationStatus,
    };
  }
  if (listingUnit) {
    return {
      ...listingUnit,
      reservation_status: booking.reservationStatus,
    };
  }
  if (bookingListingUnit) {
    return {
      ...bookingListingUnit,
      reservation_status: booking.reservationStatus,
    };
  }
  if (previousSameUnit) {
    return {
      ...previousSameUnit.unit,
      reservation_status: booking.reservationStatus,
    };
  }
  return {
    unit_id: booking.unitId,
    unit_number: booking.unitNumberHint || booking.unitId,
    unit_type: '',
    number_of_bedrooms: '',
    number_of_bathrooms: 0,
    total_price: 0,
    total_area: 0,
    latitude: 0,
    longitude: 0,
    reservation_status: booking.reservationStatus,
  };
}

/**
 * Map an API active booking into store UI state.
 *
 * Unit row priority:
 * 1) Current listing replica
 * 2) Row built from booking details payload
 * 3) Previous sticky snapshot
 * 4) Minimal stub
 */
export function buildPendingReservationFromBooking(
  booking: AdhaActiveBooking,
  options: BuildPendingReservationOptions,
): PendingUnitReservation {
  const {
    availableUnits,
    previous,
    resolveUnitTitle,
    formatRequestedOn,
    bookingListingUnit,
  } = options;

  const listingUnit = availableUnits.find((unit) => unit.unit_id === booking.unitId);
  const previousSameUnit =
    previous?.unitId === booking.unitId ? previous : null;
  const unit = resolveUnitForBooking(
    booking,
    listingUnit,
    previousSameUnit,
    bookingListingUnit,
  );

  let unitTitle = unit.unit_number || booking.unitId;
  if (listingUnit || bookingListingUnit) {
    unitTitle = resolveUnitTitle(unit) || unitTitle;
  } else if (previousSameUnit?.unitTitle) {
    unitTitle = previousSameUnit.unitTitle;
  } else {
    unitTitle = resolveUnitTitle(unit) || unitTitle;
  }

  let requestedOn = '';
  if (booking.requestedOnRaw) {
    requestedOn =
      formatRequestedOn?.(booking.requestedOnRaw) || booking.requestedOnRaw;
  }

  return {
    unitId: booking.unitId,
    unitTitle,
    requestedOn,
    unit,
    reservationStatus: booking.reservationStatus,
    ...(booking.parentId ? { parentId: booking.parentId } : {}),
  };
}

/**
 * Apply GET /v1/bookings result to the store.
 * - Active booking → pending + seed unit only if missing from catalogue
 * - "Booking not found." → clear booking only (keep unit row)
 */
export function applyAdhaBookingApiResult(params: {
  response: BookingResponse | null | undefined;
  availableUnits: readonly AvailableUnit[];
  resolveUnitTitle: (unit: AvailableUnit) => string;
  formatRequestedOn?: (raw: string) => string;
  profileDetails?: ProfileDetailsResponse | null;
  /** Login API's `convertedLead` — true reads profile-details `status` instead of `current_status`. */
  convertedLead?: boolean;
  setPendingUnitReservation: (reservation: PendingUnitReservation) => void;
  clearAdhaWebBookingState: () => void;
  getPreviousPending: () => PendingUnitReservation | null;
  upsertAvailableUnitFirst: (unit: AvailableUnit) => void;
}): boolean {
  const formatRequestedOn = params.formatRequestedOn ?? ((raw) => raw);
  const active = parseActiveBookingFromApi(params.response);
  if (!active) {
    params.clearAdhaWebBookingState();
    return false;
  }

  const bookingRecord = extractBookingDetails(params.response) as Record<
    string,
    unknown
  > | null;
  const bookingListingUnit = bookingRecord
    ? availableUnitFromBookingRecord(bookingRecord, active.reservationStatus)
    : null;

  const built = buildPendingReservationFromBooking(active, {
    availableUnits: params.availableUnits,
    previous: params.getPreviousPending(),
    resolveUnitTitle: params.resolveUnitTitle,
    formatRequestedOn,
    bookingListingUnit,
  });
  const pending = overlayProfileBookingOnPending(
    built,
    params.profileDetails,
    formatRequestedOn,
    active.requestedOnRaw,
    params.convertedLead,
  );

  params.setPendingUnitReservation(pending);
  seedAvailableUnitIfMissing(
    pending.unit,
    params.availableUnits,
    params.upsertAvailableUnitFirst,
  );
  return true;
}
